import { clearEnrollmentBrowserState } from '../../../state/enrollmentBrowserStorage'
import { hasWidgetLeadData } from '../../../state/widgetLeadStorage'
import { readEnrollmentDraftSnapshot } from '../../../utils/enrollmentDraftData'
import {
  getEmptyBootstrapFields,
  readEnrollmentDraftBootstrapFields,
  readWidgetLeadBootstrapFields,
  shouldUseEnrollmentDraftBootstrap
} from '../../../utils/enrollmentBootstrapSources'
import type { ApplicationFormData, StoredEnrollmentFormData } from '../types'
import { formatBirthDate, normalizeEmail } from '.'
import { getApplicationProfileFields, getStoredUserProfile } from './profile'

export const readStoredEnrollmentFormData = (): StoredEnrollmentFormData => {
  return readEnrollmentDraftSnapshot()
}

export const getInitialFormData = (): ApplicationFormData => {
  const widgetLead = readWidgetLeadBootstrapFields()
  const storedEnrollmentCandidate = readStoredEnrollmentFormData()
  const profile = getStoredUserProfile()
  const profileFields = getApplicationProfileFields(profile)
  const shouldUseStoredDraft = shouldUseEnrollmentDraftBootstrap({
    widgetLeadEmail: widgetLead.email,
    draftEmail: storedEnrollmentCandidate.email,
    normalizeEmail
  })
  const storedEnrollment = shouldUseStoredDraft ? storedEnrollmentCandidate : {}
  const draftFields = shouldUseStoredDraft
    ? readEnrollmentDraftBootstrapFields()
    : getEmptyBootstrapFields()

  const streetAddress =
    storedEnrollment.addressLine1?.trim() ||
    draftFields.addressLine1 ||
    profileFields.addressLine1 ||
    widgetLead.addressLine1 ||
    widgetLead.address ||
    ''
  const addressCity =
    storedEnrollment.city || draftFields.city || profileFields.city || widgetLead.city || ''
  const addressState =
    storedEnrollment.state || draftFields.state || profileFields.state || widgetLead.state || ''
  const addressZip =
    storedEnrollment.zipCode || draftFields.zipCode || profileFields.zipCode || widgetLead.zipCode || ''

  return {
    firstName:
      storedEnrollment.firstName ||
      draftFields.firstName ||
      profileFields.firstName ||
      widgetLead.firstName ||
      '',
    lastName:
      storedEnrollment.lastName ||
      draftFields.lastName ||
      profileFields.lastName ||
      widgetLead.lastName ||
      '',
    middleName: draftFields.middleName || profileFields.middleName || widgetLead.middleName || '',
    phoneNumber:
      storedEnrollment.phone ||
      draftFields.phoneNumber ||
      profileFields.phoneNumber ||
      widgetLead.phoneNumber ||
      '',
    email: widgetLead.email || storedEnrollment.email || profileFields.email || draftFields.email || '',
    addressLine1: streetAddress,
    city: addressCity,
    state: addressState,
    zipCode: addressZip,
    birthDate: formatBirthDate(
      storedEnrollment.dateOfBirth || draftFields.birthDate || profileFields.birthDate || ''
    ),
    about: widgetLead.about || '',
    termsAccepted:
      storedEnrollment.termsAccepted ??
      storedEnrollment.smsConsent ??
      (typeof profileFields.smsConsent === 'boolean' ? profileFields.smsConsent : false),
    smsConsent:
      storedEnrollment.smsConsent ??
      (typeof profileFields.smsConsent === 'boolean' ? profileFields.smsConsent : false),
    marketingConsent: storedEnrollment.marketingConsent ?? false
  }
}

export const getBlankApplicationFormData = (email: string): ApplicationFormData => ({
  firstName: '',
  lastName: '',
  middleName: '',
  phoneNumber: '',
  email,
  addressLine1: '',
  city: '',
  state: '',
  zipCode: '',
  birthDate: '',
  about: '',
  termsAccepted: false,
  smsConsent: false,
  marketingConsent: false
})

export const clearUnauthenticatedApplicationStorage = () => {
  if (typeof window === 'undefined') return
  if (hasWidgetLeadData()) return
  clearEnrollmentBrowserState()
}
